/**
 * One animation clock shared by every spinner that opts in, so a spinner that replaces another
 * (a loading state handing over to the next one) carries on from the same moment rather than
 * replaying its burst. Times are milliseconds on the `performance.now()` timeline, which
 * `requestAnimationFrame` timestamps share.
 */

/** A spinner that starts within this long of the last one leaving continues its animation. */
export const RESUME_WINDOW_MS = 250;

let epochMs: number | null = null;
let running = 0;
let lastLeftMs = Number.NEGATIVE_INFINITY;

const isRunning = (nowMs: number): boolean =>
	epochMs !== null && (running > 0 || nowMs - lastLeftMs <= RESUME_WINDOW_MS);

/**
 * Joins the clock if an animation is on screen or has only just left it, and returns when it
 * started. Returns `null` when there is none to continue, so the caller starts afresh.
 */
export const resumeAnimationClock = (nowMs: number): number | null => {
	if (!isRunning(nowMs)) return null;

	running += 1;
	return epochMs;
};

/** Joins the clock, starting it now unless an animation is already running. */
export const startAnimationClock = (nowMs: number): number => {
	if (!isRunning(nowMs) || epochMs === null) epochMs = nowMs;

	running += 1;
	return epochMs;
};

/** Leaves the clock. The last spinner to leave opens the window for the next to resume in. */
export const leaveAnimationClock = (nowMs: number): void => {
	running = Math.max(0, running - 1);

	if (running === 0) lastLeftMs = nowMs;
};

/** Forgets the running animation. For tests, which share this module between cases. */
export const resetAnimationClock = (): void => {
	epochMs = null;
	running = 0;
	lastLeftMs = Number.NEGATIVE_INFINITY;
};
